// React, Next
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import Link from 'next/link'

// MUI
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

// Custom
import Section from '@/components/sections/Section'
import { translateHref } from '@/assets/functions/links'
import { GlobalContext } from 'context/GlobalContextProvider'

// Config
import nextI18nextConfig from 'next-i18next.config'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBody, getFontBodySmall, getFontH2 } from '@/assets/theme/fonts'

export default function CtaSection() {
  const { t } = useTranslation('servicesOtAwarenessTraining')
  const { screenDimensions } = useContext(GlobalContext)

  const router = useRouter()
  const currentLocale =
    router.query.locale || nextI18nextConfig.i18n.defaultLocale

  const stacked = screenDimensions.width < breakpoints.desktopToMobile

  const getContent = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: stacked ? 'column' : 'row',
        alignItems: stacked ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        gap: stacked ? '2rem' : '3rem',
        width: '100%',
        background: themeColors.kroemtech.dunkelblau,
        borderRadius: '10px',
        padding: stacked ? '2.5rem 1.75rem' : '3rem',
        boxSizing: 'border-box'
      }}
    >
      <Box sx={{ maxWidth: '42rem' }}>
        <Typography
          variant="h2"
          component="h2"
          fontSize={getFontH2()}
          sx={{
            fontWeight: 700,
            lineHeight: 1.2,
            color: '#fff',
            marginBottom: '1.25rem'
          }}
        >
          {t('cta.title')}
        </Typography>

        <Typography
          sx={{
            fontSize: getFontBody(),
            lineHeight: 1.7,
            color: 'rgba(255,255,255,0.8)',
            marginBottom: '0.85rem'
          }}
        >
          {t('cta.description1')}
        </Typography>

        <Typography
          sx={{
            fontSize: getFontBody(),
            lineHeight: 1.7,
            color: 'rgba(255,255,255,0.8)'
          }}
        >
          {t('cta.description2')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '0.9rem',
          flexShrink: 0
        }}
      >
        <Link href={translateHref(currentLocale, 'contact')}>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            sx={{
              background: themeColors.success.main,
              borderRadius: '4px',
              padding: '0.9rem 1.75rem',
              fontSize: '1rem',
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: 'none',
              whiteSpace: 'nowrap',
              '&:hover': {
                background: themeColors.success.darker,
                boxShadow: 'none'
              }
            }}
          >
            {t('cta.button')}
          </Button>
        </Link>

        <Typography
          sx={{
            fontSize: getFontBodySmall(),
            lineHeight: 1.5,
            color: 'rgba(255,255,255,0.7)',
            maxWidth: '16rem'
          }}
        >
          {t('cta.note')}
        </Typography>
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.kroemtech.background}
      paddingTop="4rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
